import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CircleX, Copy, Download } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  cancelExceptionLabel,
  createExport,
  getExportStatus,
  fetchExceptionLabelCloneContext,
  fetchExceptionLabels,
} from '../../services/shipturtleApi';
import { useTaraTerminalFocus } from '../../utils/taraTerminalContext';
import { statusLabel } from './exceptionLabelStatus';
import { progressTone } from './shipmentProgressTone';
import './ExceptionLabelsTable.css';

/**
 * Every return and replacement label, for ops and accounts.
 *
 * Rows are *labels*, not orders: one order can hold a return and a replacement,
 * each with its own AWB, documents and the internal note explaining why it was
 * raised. That is why this cannot be another tab on the orders list.
 */
// 25 keeps a page scannable without scrolling, and matches the Orders tab.
const PAGE_SIZE = 25;

function DocumentsButton({ row, onDownload, busy }) {
  // Prozo issues no shipping label for a reverse leg — only a manifest — so the
  // button is named for what will actually arrive, and stays enabled whenever
  // any document exists.
  const label = row.has_label && row.has_manifest
    ? 'Label & Manifest'
    : row.has_manifest
      ? 'Manifest'
      : row.has_label
        ? 'Label'
        : 'Preparing…';
  const available = row.has_label || row.has_manifest;

  return (
    <button
      type="button"
      className="exLabelsDocsBtn"
      disabled={!available || busy}
      onClick={() => onDownload(row)}
      title={available ? `Download ${label}` : 'Documents are still being prepared'}
    >
      {busy ? 'Downloading…' : label}
    </button>
  );
}

/**
 * The label's live Prozo status, linking to Prozo's own tracking page.
 *
 * Replaces the old separate Status column and "Track" link: both answered the
 * same question, and the status was an uncoloured string that read as noise.
 */
export function StatusPill({ row }) {
  const tone = progressTone(row);
  const text = statusLabel(row);
  const className = `exLabelsStatus ${tone}`;

  // Nothing to link to before the first scan, or once the label is dead.
  if (!row.tracking_link || tone === 'isDead') {
    return <span className={className}>{text}</span>;
  }
  return (
    <a
      className={className}
      href={row.tracking_link}
      target="_blank"
      rel="noopener noreferrer"
      title="Track on Prozo"
    >
      {text}
    </a>
  );
}

/**
 * The ⋮ row menu: download, clone, cancel.
 *
 * Rendered in a portal on <body> rather than inside the row. A table cell sits
 * inside the table's own box, so an absolutely positioned menu was clipped by
 * it — the list grew a scrollbar just to reach a menu on a single-row table.
 * Positioning is therefore computed from the trigger's viewport rect.
 */
function RowMenu({ row, onDownload, onClone, onCancel, busy }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  const place = useCallback(() => {
    const trigger = ref.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const MENU_H = 150;
    // Flip above the trigger when there is not room below, so the last row's
    // menu is not pinned off-screen.
    const openUp = rect.bottom + MENU_H > window.innerHeight && rect.top > MENU_H;
    setCoords({
      top: openUp ? rect.top - MENU_H - 4 : rect.bottom + 4,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onDocClick = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // Scrolling or resizing would leave the menu floating away from its row.
    const onReflow = () => setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open, place]);

  const isCancelled = row.status === 'cancelled';
  const hasDocs = row.has_label || row.has_manifest;
  // Named for what will actually arrive: Prozo issues no label for a reverse leg.
  const docsLabel = row.has_label && row.has_manifest
    ? 'Download label & manifest'
    : row.has_manifest
      ? 'Download manifest'
      : 'Download label';

  return (
    <div className="exLabelsMenu" ref={ref}>
      <button
        type="button"
        className="exLabelsMenuTrigger"
        aria-label="More actions"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ⋮
      </button>
      {open && coords && createPortal(
        <div
          className="exLabelsMenuList"
          role="menu"
          ref={menuRef}
          style={{ top: coords.top, right: coords.right }}
        >
          <button
            type="button"
            role="menuitem"
            disabled={!hasDocs || busy}
            onClick={() => { setOpen(false); onDownload(row); }}
          >
            <Download size={16} aria-hidden="true" />
            {busy ? 'Downloading…' : hasDocs ? docsLabel : 'Documents preparing…'}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onClone(row); }}
          >
            <Copy size={16} aria-hidden="true" />
            Clone
          </button>
          {/* A cancelled label has nothing left to cancel. */}
          {!isCancelled && (
            <button
              type="button"
              role="menuitem"
              className="isDanger"
              onClick={() => { setOpen(false); onCancel(row); }}
            >
              <CircleX size={16} aria-hidden="true" />
              Cancel label
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * Cancel confirmation. The reason is mandatory for the same purpose as the
 * generation note: the freight may already have been charged, and accounts has
 * to be able to tell later why the label went away.
 */
function CancelDialog({ row, onClose, onConfirm, submitting }) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  if (!row) return null;
  const valid = reason.trim().length > 0;

  return (
    <div className="exLabelsModalBackdrop" onClick={submitting ? undefined : onClose} role="presentation">
      <div
        className="exLabelsModal exLabelsModalNarrow"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="exLabelsModalHead">
          <div className="exLabelsModalTitle">Cancel this label?</div>
        </div>
        <div className="exLabelsModalBody">
          <p className="exLabelsCancelIntro">
            AWB <strong>{row.awb_number}</strong> on order <strong>{row.order_number}</strong> will
            be cancelled with the courier. The order itself is not affected, and the items on this
            label become available to ship again.
          </p>
          <label className="exLabelsDetailLabel" htmlFor="cancelReason">
            Why is it being cancelled? <span className="exLabelsReq">required</span>
          </label>
          <textarea
            id="cancelReason"
            className={`exLabelsTextarea ${touched && !valid ? 'hasError' : ''}`}
            rows={3}
            value={reason}
            placeholder="e.g. Raised in error — customer had already returned the item."
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
          />
          {touched && !valid && (
            <div className="exLabelsFieldError">A reason is required.</div>
          )}
        </div>
        <div className="exLabelsModalFoot">
          <button type="button" className="exLabelsDocsBtn" onClick={onClose} disabled={submitting}>
            Keep label
          </button>
          <button
            type="button"
            className="exLabelsDangerBtn"
            disabled={!valid || submitting}
            onClick={() => onConfirm(row, reason.trim())}
          >
            {submitting ? 'Cancelling…' : 'Cancel label'}
          </button>
        </div>
      </div>
    </div>
  );
}

const FILTER_LABELS = {
  progress: 'Status',
  charge_bearer: 'Charge to',
  status: 'Label state',
  vendor_ids: 'Vendor',
  order_number: 'Order',
  customer_name: 'Customer',
  customer_phone: 'Phone',
  date_from: 'Generated from',
  date_to: 'Generated to',
  delivered_from: 'Delivered from',
  delivered_to: 'Delivered to',
};

function MultiSelect({ label, options, selected, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const chosen = selected || [];
  return (
    <div className="exLabelsField">
      <label>{label}</label>
      <div className="exLabelsMulti" ref={ref}>
        <button type="button" className="exLabelsMultiTrigger" onClick={() => setOpen((v) => !v)}>
          <span>{chosen.length ? `${chosen.length} selected` : `All`}</span>
          <span aria-hidden="true">▾</span>
        </button>
        {open && (
          <div className="exLabelsMultiMenu">
            {options.map((opt) => (
              <label key={opt.value} className="exLabelsMultiOption">
                <input
                  type="checkbox"
                  checked={chosen.includes(opt.value)}
                  onChange={(e) => onToggle(opt.value, e.target.checked)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Advanced filters, mirroring the Forward Order screen's pattern.
 *
 * Two-tier state on purpose: the modal edits a draft and only commits on Apply,
 * so a half-built filter set never reaches the API.
 */
function FiltersModal({ open, applied, options, onClose, onApply }) {
  const [draft, setDraft] = useState(applied);

  useEffect(() => { if (open) setDraft(applied); }, [open, applied]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));
  const toggleMulti = (key, value, checked) =>
    setDraft((prev) => {
      const current = prev[key] || [];
      const next = checked ? [...current, value] : current.filter((v) => v !== value);
      return { ...prev, [key]: next };
    });

  const apply = () => {
    // Drop empties so the chip bar and the request only carry real filters.
    const clean = {};
    Object.entries(draft).forEach(([key, value]) => {
      if (Array.isArray(value) ? value.length : value !== '' && value != null) {
        clean[key] = Array.isArray(value) ? value.join(',') : value;
      }
    });
    onApply(clean);
  };

  return (
    <div className="exLabelsModalBackdrop" onClick={onClose} role="presentation">
      <div
        className="exLabelsModal"
        role="dialog"
        aria-modal="true"
        aria-label="Filter exception labels"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="exLabelsModalHead">
          <div className="exLabelsModalTitle">Filters</div>
          <button type="button" className="exLabelsModalClose" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="exLabelsModalBody">
          <div className="exLabelsFilterGrid">
            <MultiSelect
              label="Status"
              options={options.shipment_progresses || []}
              selected={draft.progress}
              onToggle={(v, c) => toggleMulti('progress', v, c)}
            />
            <MultiSelect
              label="Charge to"
              options={options.charge_bearers || []}
              selected={draft.charge_bearer}
              onToggle={(v, c) => toggleMulti('charge_bearer', v, c)}
            />
            <MultiSelect
              label="Label state"
              options={[
                { value: 'active', label: 'Active' },
                { value: 'superseded', label: 'Filed as history' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
              selected={draft.status}
              onToggle={(v, c) => toggleMulti('status', v, c)}
            />
            <MultiSelect
              label="Carrier"
              options={(options.carriers || []).map((c) => ({
                value: String(c.id ?? c.value),
                label: c.name ?? c.label,
              }))}
              selected={draft.carrier_ids}
              onToggle={(v, c) => toggleMulti('carrier_ids', v, c)}
            />

            <div className="exLabelsField">
              <label htmlFor="fOrder">Order number</label>
              <input
                id="fOrder"
                value={draft.order_number || ''}
                onChange={(e) => setField('order_number', e.target.value)}
              />
            </div>
            <div className="exLabelsField">
              <label htmlFor="fCustomer">Customer name</label>
              <input
                id="fCustomer"
                value={draft.customer_name || ''}
                onChange={(e) => setField('customer_name', e.target.value)}
              />
            </div>
            <div className="exLabelsField">
              <label htmlFor="fPhone">Customer phone</label>
              <input
                id="fPhone"
                value={draft.customer_phone || ''}
                onChange={(e) => setField('customer_phone', e.target.value)}
              />
            </div>

            <div className="exLabelsField">
              <label htmlFor="fFrom">Generated from</label>
              <input
                id="fFrom"
                type="date"
                value={draft.date_from || ''}
                onChange={(e) => setField('date_from', e.target.value)}
              />
            </div>
            <div className="exLabelsField">
              <label htmlFor="fTo">Generated to</label>
              <input
                id="fTo"
                type="date"
                value={draft.date_to || ''}
                onChange={(e) => setField('date_to', e.target.value)}
              />
            </div>
            <div className="exLabelsField">
              <label htmlFor="fDelFrom">Delivered from</label>
              <input
                id="fDelFrom"
                type="date"
                value={draft.delivered_from || ''}
                onChange={(e) => setField('delivered_from', e.target.value)}
              />
            </div>
            <div className="exLabelsField">
              <label htmlFor="fDelTo">Delivered to</label>
              <input
                id="fDelTo"
                type="date"
                value={draft.delivered_to || ''}
                onChange={(e) => setField('delivered_to', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="exLabelsModalFoot">
          <button type="button" className="exLabelsDocsBtn" onClick={() => setDraft({})}>
            Clear all
          </button>
          <button type="button" className="exLabelsDocsBtn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="exLabelsExportBtn" onClick={apply}>
            Apply filters
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, children, wide }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div className={`exLabelsDetail ${wide ? 'isWide' : ''}`}>
      <div className="exLabelsDetailLabel">{label}</div>
      <div className="exLabelsDetailValue">{children}</div>
    </div>
  );
}

/**
 * Everything about one exception label, in full.
 *
 * The list has to truncate the internal note to keep rows scannable, but that
 * note is the reason accounts opens this screen at all — so the whole row opens
 * this, where nothing is cut off.
 */
function ShipmentDetailModal({ row, onClose, onDownload, onClone, onCancel, busy }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!row) return null;

  const dims = row.dimensions || {};
  const hasDims = dims.length_cm && dims.width_cm && dims.height_cm;
  const isReverse = row.shipment_type === 'reverse';

  const fmt = (iso) =>
    iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';

  return (
    <div className="exLabelsModalBackdrop" onClick={onClose} role="presentation">
      <div
        className="exLabelsModal"
        role="dialog"
        aria-modal="true"
        aria-label={`Shipment ${row.awb_number}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="exLabelsModalHead">
          <div>
            <div className="exLabelsModalTitle">
              {row.order_number}
              <span className={`exLabelsLeg ${isReverse ? 'isReverse' : 'isForward'}`}>
                {isReverse ? 'Reverse' : 'Forward'}
              </span>
            </div>
            <div className="exLabelsModalSub">
              {row.vendor_id ? (
                <a
                  className="exLabelsVendorLink"
                  href={`/suspire-terminal/vendors?vendor_id=${row.vendor_id}`}
                  title="Open vendor details"
                >
                  {row.vendor_name}
                </a>
              ) : (
                row.vendor_name
              )}
            </div>
          </div>
          <button type="button" className="exLabelsModalClose" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="exLabelsModalBody">
          {/* The reason this label exists, first and uncut. */}
          <div className="exLabelsNoteBlock">
            <div className="exLabelsDetailLabel">Internal note — why this label was raised</div>
            {row.internal_note ? (
              <p className="exLabelsNoteText">{row.internal_note}</p>
            ) : (
              <p className="exLabelsNoteText isMissing">
                Not recorded. Labels raised before the note became mandatory have none.
              </p>
            )}
          </div>

          <div className="exLabelsDetailGrid">
            <Detail label="AWB">{row.awb_number}</Detail>
            <Detail label="Carrier">{row.shipping_partner || row.carrier}</Detail>
            <Detail label="Status"><StatusPill row={row} /></Detail>
            <Detail label="Charge bearer">{row.charge_bearer}</Detail>
            <Detail label="Label generated">{fmt(row.label_generated_at)}</Detail>
            <Detail label="Delivered">{fmt(row.delivered_at)}</Detail>
            <Detail label="Last tracking update">{fmt(row.progress_updated_at)}</Detail>
            <Detail label="Prozo reference">{row.prozo_reference}</Detail>
            {hasDims && (
              <Detail label="Package">
                {row.weight_kg ? `${Math.round(parseFloat(row.weight_kg) * 1000)} g · ` : ''}
                {dims.length_cm}×{dims.width_cm}×{dims.height_cm} cm
              </Detail>
            )}
            {/* Customer-facing, and distinct from the internal note above. */}
            <Detail label="Return reason (sent to courier)" wide>{row.return_reason}</Detail>
            <Detail label="Cancelled" wide>
              {row.cancelled_at ? `${fmt(row.cancelled_at)} — ${row.cancel_reason || 'no reason given'}` : null}
            </Detail>
          </div>

          {(row.ship_from?.city || row.ship_to?.city) && (
            <div className="exLabelsRoute">
              <div>
                <div className="exLabelsDetailLabel">From</div>
                <div className="exLabelsDetailValue">
                  {row.ship_from?.name}
                  <br />
                  {[row.ship_from?.city, row.ship_from?.state, row.ship_from?.pincode]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              </div>
              <div className="exLabelsRouteArrow">→</div>
              <div>
                <div className="exLabelsDetailLabel">To</div>
                <div className="exLabelsDetailValue">
                  {row.ship_to?.name}
                  <br />
                  {[row.ship_to?.city, row.ship_to?.state, row.ship_to?.pincode]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              </div>
            </div>
          )}

          {row.line_items?.length > 0 && (
            <div>
              <div className="exLabelsDetailLabel">Items ({row.line_items.length})</div>
              <ul className="exLabelsItems">
                {row.line_items.map((li) => (
                  <li key={li.line_item_id}>
                    <span>{li.product_name || li.sku || li.line_item_id}</span>
                    <span className="exLabelsQty">×{li.quantity}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="exLabelsModalFoot">
          {/* Same actions as the row menu, so the two views cannot disagree
              about what can be done to a label. */}
          <button type="button" className="exLabelsDocsBtn" onClick={() => onClone(row)}>
            Clone
          </button>
          {row.status !== 'cancelled' && (
            <button
              type="button"
              className="exLabelsDangerBtn"
              onClick={() => onCancel(row)}
            >
              Cancel label
            </button>
          )}
          <DocumentsButton row={row} onDownload={onDownload} busy={busy} />
        </div>
      </div>
    </div>
  );
}

export default function ExceptionLabelsTable({ showToast }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState([]);
  // "Every label matching the current filters", including pages never loaded.
  // Sent as select_all so the server re-runs the filters rather than trusting a
  // list of ids it would have to page through to build.
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [detailRow, setDetailRow] = useState(null);
  const [cancelRow, setCancelRow] = useState(null);
  useTaraTerminalFocus('open_label', detailRow && {
    order_number: detailRow.order_number,
    awb: detailRow.awb_number,
    leg: detailRow.shipment_type,
  });
  // `filters` is what is applied; the modal edits a draft and commits on Apply,
  // so half-built filter sets never hit the API.
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!showFilters || Object.keys(filterOptions).length) return;
    (async () => {
      try {
        const api = await import('../../api/api');
        setFilterOptions((await api.fetchOrderFilterOptions()) || {});
      } catch {
        // A failed options load leaves the free-text and date filters usable.
        setFilterOptions({});
      }
    })();
  }, [showFilters, filterOptions]);
  const [cancelling, setCancelling] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportReady, setExportReady] = useState(null);
  const [search, setSearch] = useState('');
  const [leg, setLeg] = useState('');

  // Debounced, so typing an order number does not fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchExceptionLabels({
        q: debouncedSearch,
        shipment_type: leg,
        ...filters,
        limit: PAGE_SIZE,
        offset,
      });
      setRows(data.labels || []);
      setCount(data.count || 0);
    } catch (err) {
      setError(err.message || 'Could not load exception labels');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, leg, filters, offset]);

  useEffect(() => { load(); }, [load]);

  const activeFilterCount = Object.keys(filters).length;

  const toggleRow = (id) => {
    setSelectAllMode(false);
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const pageIds = rows.map((r) => r.id);
  // Compared against this page's ids rather than a bare length, so a stale id
  // from a previous page cannot make the header checkbox lie.
  const allSelected = rows.length > 0 && pageIds.every((id) => selected.includes(id));
  const toggleAll = () => {
    setSelectAllMode(false);
    setSelected(allSelected ? [] : pageIds);
  };

  const clearSelection = () => {
    setSelected([]);
    setSelectAllMode(false);
  };

  const selectionCount = selectAllMode ? count : selected.length;

  const handleDownload = async (row) => {
    setDownloading((prev) => ({ ...prev, [row.id]: true }));
    try {
      const api = await import('../../api/api');
      const resp = await api.downloadShipmentCombinedDocuments(row.id);
      if (resp?.success && resp.combined_url) {
        const suffix = (resp.documents || []).join('_') || 'documents';
        await api.downloadFileFromUrl(
          resp.combined_url,
          resp.filename || `${row.order_number}_${row.awb_number}_${suffix}.pdf`
        );
      } else {
        showToast?.({ type: 'error', message: resp?.message || 'Failed to download documents' });
      }
    } catch (err) {
      showToast?.({ type: 'error', message: err.message || 'Failed to download documents' });
    } finally {
      setDownloading((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const handleCancel = async (row, reason) => {
    setCancelling(true);
    try {
      const resp = await cancelExceptionLabel(row.id, reason);
      // The label is cancelled locally either way; say so plainly when the
      // courier did not confirm, rather than reporting a clean success.
      showToast?.({
        type: resp?.prozo_cancel_succeeded ? 'success' : 'warning',
        message: resp?.message || 'Label cancelled',
      });
      setCancelRow(null);
      setDetailRow(null);
      load();
    } catch (err) {
      showToast?.({ type: 'error', message: err.message || 'Could not cancel this label' });
    } finally {
      setCancelling(false);
    }
  };

  const handleClone = async (row) => {
    try {
      const context = await fetchExceptionLabelCloneContext(row.id);
      // Handed to the label screen through navigation state; it is a prefill,
      // not a saved draft, so it deliberately does not go in the URL.
      navigate('/suspire-terminal/labels', { state: { cloneFrom: context } });
    } catch (err) {
      showToast?.({ type: 'error', message: err.message || 'Could not load that label' });
    }
  };

  // A changed filter, search or leg means the selection refers to rows that may
  // no longer be listed, so it is dropped rather than exporting the invisible.
  useEffect(() => {
    setSelected([]);
    setSelectAllMode(false);
  }, [debouncedSearch, leg, filters]);

  const handleExport = async () => {
    setExporting(true);
    setExportReady(null);
    try {
      const job = await createExport(
        'exception_labels',
        'xlsx',
        selectAllMode || selected.length === 0 ? [] : selected,
        selectAllMode || selected.length === 0,
        // Every applied filter, not just the leg: the export must contain the
        // rows the admin was looking at when they pressed it.
        { q: debouncedSearch, shipment_type: leg, ...filters },
        {},
        null
      );
      const jobId = job?.jobId || job?.job_id || job?.id;
      if (!jobId) throw new Error('Export did not start.');

      // Poll rather than hold the request open, and surface a download card —
      // waiting on an email for a file the admin asked for just now is a poor
      // trade. Gives up after ~2 minutes so a dead worker cannot spin forever.
      for (let i = 0; i < 60; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 2000));
        // eslint-disable-next-line no-await-in-loop
        const st = await getExportStatus(jobId);
        if (st?.status === 'ready' && st?.downloadUrl) {
          setExportReady({ url: st.downloadUrl, filename: st.filename || 'exception_labels.xlsx' });
          return;
        }
        if (st?.status === 'failed') throw new Error(st?.error || 'Export failed.');
      }
      throw new Error('Export is taking longer than expected. Check back shortly.');
    } catch (err) {
      showToast?.({ type: 'error', message: err.message || 'Could not start export' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="exLabelsWrap">
      <div className="exLabelsToolbar">
        <input
          type="text"
          className="exLabelsSearch"
          placeholder="Order number or AWB"
          value={search}
          onChange={(e) => { setOffset(0); setSearch(e.target.value); }}
        />
        <select
          className="exLabelsSelect"
          value={leg}
          onChange={(e) => { setOffset(0); setLeg(e.target.value); }}
        >
          <option value="">All legs</option>
          <option value="forward">Forward (replacement)</option>
          <option value="reverse">Reverse (return)</option>
        </select>
        <div className="exLabelsToolbarRight">
          <button
            type="button"
            className={`exLabelsFilterBtn ${activeFilterCount ? 'hasActive' : ''}`}
            onClick={() => setShowFilters(true)}
          >
            Filters
            {activeFilterCount > 0 && <span className="exLabelsFilterBadge">{activeFilterCount}</span>}
          </button>
          <span className="exLabelsCount">
            {selected.length > 0 ? `${selected.length} selected` : `${count} labels`}
          </span>
          <button
            type="button"
            className="exLabelsExportBtn"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting
              ? 'Exporting…'
              : selectionCount > 0 && !selectAllMode
                ? `Export ${selectionCount}`
                : 'Export'}
          </button>
        </div>
      </div>

      {allSelected && count > rows.length && (
        <div className="exLabelsSelectAllBar">
          {selectAllMode ? (
            <>
              <span>All <strong>{count}</strong> matching labels are selected.</span>
              <button type="button" onClick={clearSelection}>Clear selection</button>
            </>
          ) : (
            <>
              <span>All <strong>{rows.length}</strong> on this page are selected.</span>
              <button type="button" onClick={() => setSelectAllMode(true)}>
                Select all {count} matching labels
              </button>
            </>
          )}
        </div>
      )}

      {activeFilterCount > 0 && (
        <div className="exLabelsChips">
          {Object.entries(filters).map(([key, value]) => (
            <span key={key} className="exLabelsChip">
              {FILTER_LABELS[key] || key}: {String(value).replace(/,/g, ', ')}
              <button
                type="button"
                onClick={() => {
                  const next = { ...filters };
                  delete next[key];
                  setOffset(0);
                  setFilters(next);
                }}
                aria-label={`Remove ${FILTER_LABELS[key] || key} filter`}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            className="exLabelsClearChips"
            onClick={() => { setOffset(0); setFilters({}); }}
          >
            Clear all
          </button>
        </div>
      )}

      {error && <div className="exLabelsError">{error}</div>}

      <div className="exLabelsTableScroll">
        <table className="exLabelsTable">
          <thead>
            <tr>
              <th className="exLabelsCheckCol">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              </th>
              <th>Order Details</th>
              <th>Courier Partner</th>
              <th>Route</th>
              <th>Package</th>
              <th>Charge To</th>
              <th>Tracking</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="exLabelsEmpty">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="exLabelsEmpty">No exception labels yet.</td></tr>
            )}
            {!loading && rows.map((row) => (
              <tr
                key={row.id}
                className={`exLabelsRow ${selected.includes(row.id) ? 'isSelected' : ''}`}
                onClick={() => setDetailRow(row)}
                title="View full details"
              >
                {/* stopPropagation on the interactive cells: ticking a row or
                    following its links must not also open the detail modal. */}
                <td className="exLabelsCheckCol" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectAllMode || selected.includes(row.id)}
                    onChange={() => toggleRow(row.id)}
                  />
                </td>
                {/* Order: number, leg, vendor, note snippet and date stacked —
                    one column instead of five, so the row stays readable
                    without horizontal scroll. */}
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="exLabelsCellStack">
                    <div className="exLabelsCellTop">
                      <button
                        type="button"
                        className="exLabelsOrderLink"
                        onClick={() => navigate(`/suspire-terminal/orders/${row.order_id}`)}
                      >
                        {row.order_number}
                      </button>
                      <span className={`exLabelsLeg ${row.shipment_type === 'reverse' ? 'isReverse' : 'isForward'}`}>
                        {row.shipment_type === 'reverse' ? 'Reverse' : 'Forward'}
                      </span>
                    </div>
                    {/* Deep-links into the vendors screen, which opens a
                        vendor's detail view straight from ?vendor_id=. */}
                    {row.vendor_id ? (
                      <button
                        type="button"
                        className="exLabelsVendorLink"
                        title="Open vendor details"
                        onClick={() =>
                          navigate(`/suspire-terminal/vendors?vendor_id=${row.vendor_id}`)
                        }
                      >
                        {row.vendor_name || 'Unknown vendor'}
                      </button>
                    ) : (
                      <div className="exLabelsCellSub">{row.vendor_name || '—'}</div>
                    )}
                    <div className="exLabelsCellSub exLabelsNoteLine" title={row.internal_note}>
                      {row.internal_note || <span className="exLabelsNoteMissing">no reason recorded</span>}
                    </div>
                    <div className="exLabelsCellMuted">
                      {row.label_generated_at
                        ? new Date(row.label_generated_at).toLocaleString(undefined, {
                            dateStyle: 'medium', timeStyle: 'short',
                          })
                        : '—'}
                    </div>
                  </div>
                </td>

                <td onClick={(e) => e.stopPropagation()}>
                  <div className="exLabelsCellStack">
                    {/* The AWB is the handle for "where is this parcel", so it
                        links to tracking rather than copying itself. Falls back
                        to plain text when Prozo has issued no link yet, so the
                        number is never a dead affordance. */}
                    {row.tracking_link ? (
                      <a
                        className="exLabelsAwb"
                        href={row.tracking_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Track this shipment"
                      >
                        {row.awb_number}
                      </a>
                    ) : (
                      <span className="exLabelsAwb">{row.awb_number}</span>
                    )}
                    <div className="exLabelsCellSub">
                      {row.shipping_partner || row.carrier || 'Unknown partner'}
                    </div>
                  </div>
                </td>

                <td>
                  <div className="exLabelsCellStack">
                    <div className="exLabelsCellSub">
                      <strong>Pickup:</strong> {row.ship_from?.name || '—'}
                      {row.ship_from?.pincode ? `, ${row.ship_from.pincode}` : ''}
                    </div>
                    <div className="exLabelsCellSub">
                      <strong>Delivery:</strong> {row.ship_to?.name || '—'}
                      {row.ship_to?.pincode ? `, ${row.ship_to.pincode}` : ''}
                    </div>
                  </div>
                </td>

                <td>
                  <div className="exLabelsCellStack">
                    <div className="exLabelsCellSub">
                      {row.weight_kg ? `${parseFloat(row.weight_kg).toFixed(2)} kg` : '—'}
                    </div>
                    {row.dimensions?.length_cm && (
                      <div className="exLabelsCellMuted">
                        {row.dimensions.length_cm} × {row.dimensions.width_cm} × {row.dimensions.height_cm} cm
                      </div>
                    )}
                    <div className="exLabelsCellMuted">
                      {(row.line_items || []).length} item{(row.line_items || []).length === 1 ? '' : 's'}
                    </div>
                  </div>
                </td>

                <td>{row.charge_bearer}</td>

                {/* Status and tracking are the same question — where is this
                    parcel — so they are one pill that links to Prozo. */}
                <td onClick={(e) => e.stopPropagation()}>
                  <StatusPill row={row} />
                </td>

                <td onClick={(e) => e.stopPropagation()}>
                  <div className="exLabelsActions">
                    <button
                      type="button"
                      className="exLabelsDetailsBtn"
                      onClick={() => setDetailRow(row)}
                    >
                      Details
                    </button>
                    <RowMenu
                      row={row}
                      onDownload={handleDownload}
                      onClone={handleClone}
                      onCancel={(r) => setCancelRow(r)}
                      busy={!!downloading[row.id]}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {exportReady && (
        <div className="exportReadyCard" role="status">
          <div className="exportReadyText">
            <div className="exportReadyTitle">Export ready</div>
            <div className="exportReadyName">{exportReady.filename}</div>
          </div>
          <a
            className="exLabelsExportBtn"
            href={exportReady.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setExportReady(null)}
          >
            Download
          </a>
          <button
            type="button"
            className="exportReadyClose"
            onClick={() => setExportReady(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <FiltersModal
        open={showFilters}
        applied={filters}
        options={filterOptions}
        onClose={() => setShowFilters(false)}
        onApply={(next) => { setOffset(0); setFilters(next); setShowFilters(false); }}
      />

      <CancelDialog
        row={cancelRow}
        onClose={() => setCancelRow(null)}
        onConfirm={handleCancel}
        submitting={cancelling}
      />

      <ShipmentDetailModal
        row={detailRow}
        onClose={() => setDetailRow(null)}
        onDownload={handleDownload}
        onClone={handleClone}
        onCancel={(r) => setCancelRow(r)}
        busy={!!(detailRow && downloading[detailRow.id])}
      />

      {count > PAGE_SIZE && (
        <div className="exLabelsPager">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </button>
          <span>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, count)} of {count}
          </span>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= count}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
